import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import axios from "axios";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const Quizzes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const courseID = courseId;
  const [students, setStudents] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [scores, setScores] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newQuiz, setNewQuiz] = useState({ name: "", maxMarks: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        // Fetch all quiz IDs
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/course/get-all-quiz-ids-of-a-course`,
          { courseID: courseID },
          { withCredentials: true }
        );

        const quizzes = response.data.quizzes || [];
        setQuizzes(quizzes);

        if (quizzes.length > 0) {
          // Fetch students from the first quiz
          const firstQuiz = quizzes[0].name;
          const getStudents = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/course/get-all-quiz-scores-given-quiz-id`,
            { quizID: firstQuiz, courseID: courseID },
            { withCredentials: true }
          );

          const students = getStudents.data.scores.map(
            (student) => student.studentID
          );
          setStudents(students || []);

          // Fetch scores for all quizzes in parallel
          const quizScoresResponses = await Promise.all(
            quizzes.map((quiz) =>
              axios.post(
                `${process.env.REACT_APP_BASE_URL}/course/get-all-quiz-scores-given-quiz-id`,
                { quizID: quiz.name, courseID: courseID },
                { withCredentials: true }
              )
            )
          );

          // Process scores data and update state immutably
          let allScores = {};
          quizScoresResponses.forEach((quizResponse) => {
            console.log(quizResponse.data);
            (quizResponse.data.scores || []).forEach((item) => {
              const studentID = item.studentID;
              const quizName = item.score?.name;
              const marks = item.score?.marks;
              console.log(studentID, quizName, marks);
              if (!allScores[studentID]) {
                allScores[studentID] = {};
              }
              allScores[studentID][quizName] = marks;
            });
          });

          setScores(allScores);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        setLoading(false);
      }
    };

    if (courseID) {
      fetchQuizData();
    }
  }, [courseID]);

  const handleAddQuiz = async () => {
    if (!newQuiz.name || !newQuiz.maxMarks) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/add-quiz`,
        {
          courseID: courseID,
          quizID: newQuiz.name,
          maxScore: parseInt(newQuiz.maxMarks),
        }, {withCredentials: true}
      );
      if (response.status === 200) {
        setQuizzes([...quizzes, newQuiz]);
        setNewQuiz({ name: "", maxMarks: "" });
        setOpenDialog(false);
      }
    } catch (error) {
      console.error("Error adding quiz:", error);
    }
  };

  const updateScore = (studentId, quizName, score) => {
    try {
      const response = axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/edit-quiz-scores`,
        {
          courseID: courseID,
          studentID: studentId,
          score: score,
          quizID: quizName,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        console.log("Score updated successfully");
        setScores((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            [quizName]: parseFloat(score),
          },
        }));
      }
    } catch (err) {
      console.log("Error updating score");
    }
  };

  const exportData = () => {
    return students.map((student) => ({
      StudentID: student.id,
      Name: student.name,
      ...quizzes.reduce(
        (acc, quiz) => ({
          ...acc,
          [`Quiz_${quiz.name}`]: scores[student.id]?.[quiz.name] || "",
        }),
        {}
      ),
    }));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  console.log(students[0]);
  console.log(quizzes[0].name);
  console.log(scores);

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h4" sx={{ color: "#4CAF50" }}>
            {courseID} - Quizzes
            <Typography
              component="span"
              variant="h6"
              sx={{ ml: 2, color: "#666" }}
            >
              Section {courseID?.split("-")[2]} | {courseID?.split("-")[1]}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenDialog(true)}
            sx={{
              backgroundColor: "#4CAF50",
              "&:hover": { backgroundColor: "#45a049" },
            }}
          >
            Add Quiz
          </Button>
          <CSVLink
            data={exportData()}
            filename={`${courseID}_quizzes.csv`}
            style={{ textDecoration: "none" }}
          >
            <Button
              startIcon={<FileDownloadIcon />}
              variant="contained"
              sx={{
                backgroundColor: "#4CAF50",
                "&:hover": { backgroundColor: "#45a049" },
              }}
            >
              Export CSV
            </Button>
          </CSVLink>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
              >
                Student ID
              </TableCell>
              {quizzes.map((quiz, index) => (
                <TableCell
                  key={index}
                  sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                >
                  {quiz.name} (Max: {quiz.maxMarks})
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {students?.map((student) => (
              <TableRow key={student} hover>
                <TableCell>{student}</TableCell>
                {quizzes?.map((quiz, index) => (
                  <TableCell key={index}>
                    <TextField
                      type="number"
                      size="small"
                      value={scores[student]?.[quiz.name]} // Ensure the input is not undefined
                      onChange={(e) => {
                        // Update the scores state as the user types
                        const newScore = e.target.value;
                        setScores((prevScores) => ({
                          ...prevScores,
                          [student]: {
                            ...prevScores[student],
                            [quiz.name]: newScore,
                          },
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateScore(student, quiz.name, e.target.value);
                        }
                      }}
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add New Quiz</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Quiz Name"
            fullWidth
            value={newQuiz.name}
            onChange={(e) => setNewQuiz({ ...newQuiz, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Maximum Marks"
            type="number"
            fullWidth
            value={newQuiz.maxMarks}
            onChange={(e) =>
              setNewQuiz({ ...newQuiz, maxMarks: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddQuiz}
            variant="contained"
            sx={{ backgroundColor: "#4CAF50" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Quizzes;
